/**
 * A 12-ish point line in a table cell: the shape of a Trends series.
 *
 * One series, so no legend: the cell's text (year ago → now, the change) names
 * it, and the line only shows the SHAPE — a steady climb, a spike that faded,
 * a seasonal saw. Marks per the house chart spec: a 2px round-capped line in
 * the one sequential hue, a 10% wash under it, an end-dot on the last point.
 * Hover finds the X: each point has a hit column wider than the mark with a
 * native tooltip, so the values are reachable without gating anything on it.
 * Text never wears the data colour; the number beside the line stays ink.
 */

/** Blue 450 on the light surface, blue 400 on the dark one — each validated at
 *  ≥3:1 against its own surface. Dark is a selected step, not a flipped colour. */
export const SERIES_HUE = 'text-[#2a78d6] dark:text-[#3987e5]';

/**
 * A magnitude bar for a table row: length is the value against the column's
 * largest, so sizes read at a glance without a separate chart. ≤ 8px thick
 * (the row is small), 4px rounded data-end and square at the baseline, in the
 * same hue as the sparkline — one sequential encoding, one colour. The number
 * itself stays in ink beside it; the bar is never the only place it lives.
 */
export function VolumeBar({
  value,
  max,
  width = 64,
  testId,
}: {
  value: number | null;
  max: number;
  width?: number;
  testId?: string;
}) {
  if (value === null || !(max > 0)) return null;
  const w = Math.max(2, Math.round((width * value) / max));
  const h = 8;
  return (
    <svg
      width={width}
      height={h}
      viewBox={`0 0 ${width} ${h}`}
      aria-hidden="true"
      className={`inline-block align-middle shrink-0 ${SERIES_HUE}`}
      data-testid={testId}
      data-width={w}
    >
      {/* Square at the baseline (x=0), rounded only at the data end. */}
      <path d={`M0,0 H${Math.max(0, w - 4)} a4,4 0 0 1 4,4 a4,4 0 0 1 -4,4 H0 Z`} fill="currentColor" />
    </svg>
  );
}
export default function Sparkline({
  points,
  width = 72,
  height = 20,
  label,
  testId,
}: {
  points: { t: string; v: number | null }[];
  width?: number;
  height?: number;
  /** What the series is, for the accessible name and each point's tooltip. */
  label: string;
  testId?: string;
}) {
  const pts = points.filter(p => p.v !== null) as { t: string; v: number }[];
  if (pts.length < 2) return null;
  const pad = 4; // room for the end-dot (r=4) at the edges
  const xs = pts.map((_, i) => pad + (i * (width - 2 * pad)) / (pts.length - 1));
  const min = Math.min(...pts.map(p => p.v));
  const max = Math.max(...pts.map(p => p.v));
  const span = max - min || 1;
  const ys = pts.map(p => pad + ((max - p.v) * (height - 2 * pad)) / span);
  const line = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${height - pad} L${xs[0].toFixed(1)},${height - pad} Z`;
  const step = xs.length > 1 ? xs[1] - xs[0] : width;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`${label}: from ${pts[0].v} to ${pts[pts.length - 1].v} over ${pts.length} points`}
      className={`inline-block align-middle shrink-0 ${SERIES_HUE}`}
      data-testid={testId}
    >
      <path d={area} fill="currentColor" fillOpacity={0.1} stroke="none" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r={4} fill="currentColor" />
      {pts.map((p, i) => (
        <rect key={p.t + i} x={xs[i] - step / 2} y={0} width={step} height={height} fill="transparent">
          <title>{`${p.t}: ${p.v}`}</title>
        </rect>
      ))}
    </svg>
  );
}
